import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';

import { explainFinding } from '@/api/findings';
import type { KbAnswerOut } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { citationLabel, formatUsdCost } from '@/lib/kb-cost';

import { useAuth } from '../auth/AuthContext';

/** "Explain this finding" (design §7.2) — RAG-backed, CRA-cited explanation of
 *  why the rule fires and what determines recoverability. Read-only; no
 *  disposition change. Renders nothing until the practitioner asks. */
export function ExplainFinding({ findingId }: { findingId: string }) {
  const { getIdToken } = useAuth();

  const explain = useMutation({
    mutationFn: async (): Promise<KbAnswerOut> =>
      explainFinding(findingId, (await getIdToken()) ?? ''),
  });

  const answer = explain.data;

  return (
    <section className="grid gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wide text-brand-slate">Explain this finding</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => explain.mutate()}
          disabled={explain.isPending}
        >
          {explain.isPending ? (
            <Spinner label="Explaining…" />
          ) : (
            <>
              <Sparkles size={14} strokeWidth={1.75} /> {answer ? 'Regenerate' : 'Explain'}
            </>
          )}
        </Button>
      </div>

      {explain.isError ? (
        <p className="text-sm text-brand-slate">Couldn't generate an explanation. Please try again.</p>
      ) : null}

      {answer ? (
        <div className="grid gap-3">
          {answer.grounded ? (
            <p className="whitespace-pre-wrap text-sm text-brand-navy">{answer.text}</p>
          ) : (
            <p className="text-sm text-brand-slate">
              The corpus didn't contain enough to explain this finding without guessing.
            </p>
          )}

          {answer.citations.length > 0 ? (
            <ul className="grid gap-1">
              {answer.citations.map((c, i) => (
                <li key={`${c.doc_code}-${c.para_range}-${i}`} className="text-sm">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-royal hover:underline"
                  >
                    {citationLabel(c.doc_code, c.para_range)}
                  </a>
                  {c.title ? <span className="text-brand-slate"> — {c.title}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}

          <p className="text-xs text-brand-slate">
            {answer.model} · {formatUsdCost(Number(answer.cost_usd))}
          </p>
        </div>
      ) : null}
    </section>
  );
}
